import axiosInstance from '../../../lib/axios'
import { API_ENDPOINTS } from '../../../constants/api'
import type {
  HandoffCreateBody,
  HandoffDeclineBody,
  ShiftCreateBody,
  ShiftDetailResponse,
  ShiftHandoffResponse,
  ShiftListResponse,
  ShiftNotesUpdateBody,
  ShiftResponse,
} from '../types/shift.types'

interface ShiftHandoffListResponse {
  handoffs: ShiftHandoffResponse[]
}

export const shiftService = {
  async getCurrent(propertyId: number): Promise<ShiftResponse | null> {
    const { data } = await axiosInstance.get<ShiftResponse | null>(
      `${API_ENDPOINTS.SHIFT_CURRENT}?property_id=${propertyId}`,
    )
    return data ?? null
  },

  async start(body: ShiftCreateBody): Promise<ShiftResponse> {
    const { data } = await axiosInstance.post<ShiftResponse>(API_ENDPOINTS.SHIFT_START, body)
    return data
  },

  async list(propertyId: number, limit = 10, before?: string): Promise<ShiftListResponse> {
    const qs = new URLSearchParams({ property_id: String(propertyId), limit: String(limit) })
    if (before) qs.set('before', before)
    const { data } = await axiosInstance.get<ShiftListResponse>(`${API_ENDPOINTS.SHIFTS}?${qs.toString()}`)
    return data
  },

  async getDetail(shiftId: number): Promise<ShiftDetailResponse> {
    const { data } = await axiosInstance.get<ShiftDetailResponse>(API_ENDPOINTS.SHIFT_BY_ID(shiftId))
    return data
  },

  async updateNotes(shiftId: number, body: ShiftNotesUpdateBody): Promise<ShiftResponse> {
    const { data } = await axiosInstance.patch<ShiftResponse>(API_ENDPOINTS.SHIFT_NOTES(shiftId), body)
    return data
  },

  async createHandoff(shiftId: number, body: HandoffCreateBody): Promise<ShiftHandoffResponse> {
    const { data } = await axiosInstance.post<ShiftHandoffResponse>(API_ENDPOINTS.SHIFT_HANDOFF(shiftId), body)
    return data
  },

  async acknowledge(handoffId: number): Promise<ShiftHandoffResponse> {
    const { data } = await axiosInstance.post<ShiftHandoffResponse>(API_ENDPOINTS.SHIFT_HANDOFF_ACK(handoffId))
    return data
  },

  async decline(handoffId: number, body: HandoffDeclineBody): Promise<ShiftHandoffResponse> {
    const { data } = await axiosInstance.post<ShiftHandoffResponse>(API_ENDPOINTS.SHIFT_HANDOFF_DECLINE(handoffId), body)
    return data
  },

  async getPendingHandoffs(): Promise<ShiftHandoffResponse[]> {
    const { data } = await axiosInstance.get<ShiftHandoffListResponse>(API_ENDPOINTS.SHIFT_HANDOFFS_PENDING)
    return data.handoffs
  },

  async getShiftHandoffs(shiftId: number): Promise<ShiftHandoffResponse[]> {
    const { data } = await axiosInstance.get<ShiftHandoffListResponse>(API_ENDPOINTS.SHIFT_HANDOFFS(shiftId))
    return data.handoffs
  },

  async forceClose(shiftId: number): Promise<ShiftResponse> {
    const { data } = await axiosInstance.delete<ShiftResponse>(API_ENDPOINTS.SHIFT_BY_ID(shiftId))
    return data
  },
}
